import { useId, useState } from 'react';

import {
  formatMoney,
  MAX_SELF_SERVE_PAGES,
  MINIMUM_ORDER,
  priceFor,
} from '@repo/contracts/services-pricing';

import { CONTACT_EMAIL } from '../content/site';
import { ButtonLink, Card } from './ui';

export function PriceCalculator() {
  const id = useId();
  const [pages, setPages] = useState(25);
  const quote = priceFor(pages);

  return (
    <Card className="p-6 md:p-8">
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <label
            className="text-ui-lg font-semibold text-text-primary"
            htmlFor={`${id}-pages`}
          >
            How many pages should we audit?
          </label>
          <p className="mt-1 text-ui text-text-secondary" id={`${id}-hint`}>
            Count distinct templates and key flows — a product page template
            audited once covers every product.
          </p>
          <div className="mt-5 flex items-center gap-4">
            <input
              aria-describedby={`${id}-hint`}
              aria-label="Pages (slider)"
              className="h-2 flex-1 cursor-pointer accent-brand-strong"
              max={300}
              min={1}
              onChange={(e) => setPages(Number(e.target.value))}
              type="range"
              value={Math.min(pages, 300)}
            />
            <input
              aria-describedby={`${id}-hint`}
              className="h-12 w-24 rounded-button border border-text-muted bg-surface px-3 text-right text-ui-lg tabular-nums text-text-primary"
              id={`${id}-pages`}
              inputMode="numeric"
              max={MAX_SELF_SERVE_PAGES}
              min={1}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) {
                  setPages(Math.min(Math.max(n, 1), MAX_SELF_SERVE_PAGES));
                }
              }}
              type="number"
              value={pages}
            />
          </div>
          <ul className="mt-6 space-y-2 border-t border-hairline pt-6 text-ui-lg text-text-secondary">
            <li>
              {formatMoney(quote.ratePerPage)} per page, whatever the size of
              your site
            </li>
            <li>{formatMoney(MINIMUM_ORDER)} minimum order</li>
          </ul>
        </div>

        <div className="flex flex-col rounded-panel bg-warm p-6">
          <p className="text-small-label uppercase text-text-secondary">
            Your estimate
          </p>
          <p
            aria-live="polite"
            className="mt-2 font-report text-report-stat font-medium tabular-nums text-text-primary"
            data-testid="calculator-total"
          >
            {formatMoney(quote.total)}
          </p>
          <p className="mt-2 text-ui-lg text-text-secondary">
            {quote.pages} {quote.pages === 1 ? 'page' : 'pages'} ×{' '}
            {formatMoney(quote.ratePerPage)}
            {quote.minimumApplied
              ? ` — minimum order ${formatMoney(MINIMUM_ORDER)}`
              : ''}
          </p>
          <p className="mt-6 text-ui text-text-secondary">
            Nothing is charged when you request an audit.
          </p>
          <div className="mt-auto pt-6">
            <ButtonLink className="w-full" to={`/request?pages=${quote.pages}`}>
              Request an audit for {quote.pages}{' '}
              {quote.pages === 1 ? 'page' : 'pages'}
            </ButtonLink>
            <p className="mt-3 text-center text-caption text-text-secondary">
              More than {MAX_SELF_SERVE_PAGES} pages?{' '}
              <a
                className="font-medium text-text-primary underline"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                Talk to us
              </a>
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
